import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import Joi from 'joi';
import { Role } from 'models/auth/role';
import Group from 'models/group';
import ResourceLink from 'models/resourceLink';
import User from 'models/user';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { areDifferent } from 'utils/array';
import { Audience_Translation } from './audience_Translation';
import { AudienceLine } from './audienceLine';

export interface IAuthObject {
  audienceId?: number | undefined;
}

export class Audience {
  audienceId: number;

  auditTrailId: number;

  trans: Audience_Translation[];

  lines: AudienceLine[];

  links: ResourceLink[];

  // The following properties are set from the translation arrays
  transIdx: number;

  name: string;

  description?: string;

  constructor() {
    this.audienceId = 0;
    this.auditTrailId = 0;
    this.trans = [];
    this.transIdx = -1;
    this.name = '';
    this.lines = [];
    this.links = [];
  }

  static getEmptyAudience() {
    return new Audience();
  }

  clone(): Audience {
    const output = new Audience();
    output.audienceId = this.audienceId;
    output.auditTrailId = this.auditTrailId;
    output.name = this.name;
    output.description = this.description;
    output.transIdx = this.transIdx;
    output.trans = [...this.trans];
    output.lines = this.lines.map((l) => l.clone());

    return output;
  }

  isEqual(item: Audience): boolean {
    if (item.audienceId !== this.audienceId) return false;
    if (item.name !== this.name) return false;
    if (item.description !== this.description) return false;

    if (
      areDifferent(item.lines, this.lines, (a: AudienceLine, b: AudienceLine) => {
        return a.isEqual(b);
      }) === true
    ) {
      return false;
    }

    return true;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().max(64).required().label(localizedFields['name']),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate(
      {
        name: this.name,
      },
      { abortEarly: false },
    );
  }

  hasLine(userId: string): boolean {
    return this.lines.some((l) => l.userId === userId);
  }

  hasLines(): boolean {
    return this.lines.some((l) => l.userId || l.groupId || l.roleId);
  }

  getUsers(): string[] {
    //return the unique users in this schema
    return [...new Set(this.lines.filter((l) => l.userId).map((l) => l.userId) as string[])];
  }

  isOrgAudience(): boolean {
    return this.trans.length === 0;
  }

  setOrgAudienceName(t: TFunction) {
    if (this.isOrgAudience()) {
      this.name = t('adminGroups:Audiences.OrgAudience.Name');
      this.description = t('adminGroups:Audiences.OrgAudience.Info');
    }
  }

  getMembers(appContext: IAppContext): (User | Group | Role)[] {
    const members: (User | Group | Role)[] = [];
    this.lines.forEach((line) => {
      if (line.userId) members.push(appContext.globalDataCache.users.get(line.userId));
      else if (line.groupId) members.push(appContext.globalDataCache.groups.get(line.groupId));
      else if (line.roleId) members.push(appContext.globalDataCache.roles.get(line.roleId));
    });

    return members;
  }
}
